// ./src/components/features/Dashboard/DashboardSummaryBar/DashboardSummaryBar.tsx
import React, { useMemo, useState } from "react";
import {
  Paper,
  Group,
  Stack,
  Text,
  Skeleton,
  SimpleGrid,
  Divider,
  SegmentedControl,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { formatCurrency } from "@/utils/formatHelpers";
import { normalizeVolumeLabel } from "@/utils/unitDisplayHelpers";
import { FleetSummary, DashboardItem } from "@/utils/api/DashboardService";

import { useFleetSummary } from "@/hooks/useFleetSummary";

import { useDashboardContext } from "@/contexts/DashboardContext";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface DashboardSummaryBarProps {
  /** Custom class name */
  className?: string;
}

/** Which view mode is selected */
type SummaryMode = "monthly" | "average";

interface SummaryStatProps {
  iconName: string;
  label: string;
  value: string;
  unit?: string;
  subValue?: string;
  color: string;
  loading?: boolean;
  positive?: boolean;
}

// ============================================
// Formatting Helpers
// ============================================

const formatNumber = (value: number, decimals: number = 1): string => {
  if (value === 0) return "0";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

const formatDistanceValue = (value: number, unit: string): string => {
  return `${formatNumber(value, 0)} ${unit}`;
};

const formatVolumeValue = (value: number, unit: string): string => {
  return `${formatNumber(value, 1)} ${normalizeVolumeLabel(unit)}`;
};

const formatCostPerDistance = (
  value: number | null,
  currency: string,
  distanceUnit: string
): string => {
  if (value === null || value <= 0) return "—";
  return `${formatCurrency(value, currency)}/${distanceUnit}`;
};

// ============================================
// Averages Aggregation (from dashboard context)
// ============================================

interface AveragesAggregates {
  totalCost: number;
  revenuesAmount: number;
  refuelsVolume: number;
  totalDistance: number;
  fuelCostPerDistance: number | null;
  runningCostPerDistance: number | null;
  currency: string;
  distanceUnit: string;
  volumeUnit: string;
}

const EMPTY_AVERAGES: AveragesAggregates = {
  totalCost: 0,
  revenuesAmount: 0,
  refuelsVolume: 0,
  totalDistance: 0,
  fuelCostPerDistance: null,
  runningCostPerDistance: null,
  currency: "USD",
  distanceUnit: "km",
  volumeUnit: "l",
};

/**
 * Aggregate rolling averages across all vehicles from dashboard items.
 * Averages are already in home currency and user units from the API.
 */
function aggregateAverages(items: DashboardItem[]): AveragesAggregates {
  let monthlyExpense = 0;
  let monthlyRevenue = 0;
  let monthlyVolume = 0;
  let monthlyMileage = 0;
  let currency = "USD";
  let distanceUnit = "km";
  let volumeUnit = "l";

  for (const item of items) {
    const avg = item.averages;
    if (!avg) continue;

    monthlyExpense += avg.monthlyExpense || 0;
    monthlyRevenue += avg.monthlyRevenue || 0;
    monthlyVolume += avg.monthlyRefuelsVolume || 0;
    monthlyMileage += avg.monthlyMileageInUserUnits || 0;

    if (avg.currency) currency = avg.currency;

    const units = item.currentMonthStats?.inUserUnits;
    if (units) {
      if (units.distanceUnit) distanceUnit = units.distanceUnit;
      if (units.volumeUnit) volumeUnit = units.volumeUnit;
    }
  }

  const fuelCostPerDistance =
    monthlyMileage > 0 ? monthlyExpense / monthlyMileage : null;

  return {
    totalCost: monthlyExpense,
    revenuesAmount: monthlyRevenue,
    refuelsVolume: monthlyVolume,
    totalDistance: monthlyMileage,
    fuelCostPerDistance,
    runningCostPerDistance: fuelCostPerDistance,
    currency,
    distanceUnit,
    volumeUnit,
  };
}

// ============================================
// Sub-components
// ============================================

const SummaryStat: React.FC<SummaryStatProps> = ({
  iconName,
  label,
  value,
  unit,
  subValue,
  color,
  loading,
  positive,
}) => {
  if (loading) {
    return (
      <Stack gap={4} align="center" style={{ minWidth: 80 }}>
        <Skeleton height={28} width={80} radius="sm" />
        <Skeleton height={16} width={60} radius="sm" />
      </Stack>
    );
  }

  const displayLabel = subValue ? `${label} (${subValue})` : label;

  return (
    <Stack gap={4} align="center" style={{ minWidth: 80 }}>
      <Group gap={6} wrap="nowrap" align="center">
        <AppIcon
          iconName={iconName}
          size={22}
          color={`var(--mantine-color-${color}-6)`}
        />
        <Text
          size="lg"
          fw={700}
          ff="var(--ff-mono)"
          c={positive ? "green" : undefined}
        >
          {positive && value !== "0" && !value.startsWith("$0") ? "+" : ""}
          {value}
        </Text>
        {unit && (
          <Text size="sm" fw={500} c="dimmed">
            {unit}
          </Text>
        )}
      </Group>
      <Text size="sm" c="dimmed" ta="center" lineClamp={1}>
        {displayLabel}
      </Text>
    </Stack>
  );
};

// ============================================
// Month Navigation Component
// ============================================

interface MonthNavigatorProps {
  year: number;
  month: number;
  isCurrentMonth: boolean;
  canGoToPreviousMonth: boolean;
  canGoToNextMonth: boolean;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onCurrentMonth: () => void;
  loading: boolean;
}

const MonthNavigator: React.FC<MonthNavigatorProps> = ({
  year,
  month,
  isCurrentMonth,
  canGoToPreviousMonth,
  canGoToNextMonth,
  onPreviousMonth,
  onNextMonth,
  onCurrentMonth,
  loading,
}) => {
  const { t } = useTranslation(["common"]);

  const monthLabel = useMemo(() => {
    const monthName = t(`common:months.${month}`);
    const now = new Date();
    return year === now.getFullYear() ? monthName : `${monthName} ${year}`;
  }, [month, year, t]);

  return (
    <Group justify="center" gap="xs" wrap="nowrap">
      <ActionIcon
        variant="subtle"
        size="lg"
        onClick={onPreviousMonth}
        disabled={!canGoToPreviousMonth || loading}
        aria-label={t("common:dashboardSummary.previousMonth")}
      >
        <AppIcon iconName="ChevronLeft" size={20} />
      </ActionIcon>

      <Text fw={600} size="lg" ta="center" style={{ minWidth: 140 }}>
        {loading ? <Skeleton height={24} width={120} radius="sm" /> : monthLabel}
      </Text>

      <ActionIcon
        variant="subtle"
        size="lg"
        onClick={onNextMonth}
        disabled={!canGoToNextMonth || loading}
        aria-label={t("common:dashboardSummary.nextMonth")}
      >
        <AppIcon iconName="ChevronRight" size={20} />
      </ActionIcon>

      {!isCurrentMonth && (
        <Tooltip label={t("common:dashboardSummary.goToCurrentMonth")}>
          <ActionIcon
            variant="light"
            size="lg"
            color="blue"
            onClick={onCurrentMonth}
            disabled={loading}
          >
            <AppIcon iconName="CalendarEvent" size={18} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Monthly Stats Content
// ============================================

interface MonthlyStatsContentProps {
  summary: FleetSummary | null;
  loading: boolean;
}

const MonthlyStatsContent: React.FC<MonthlyStatsContentProps> = ({
  summary,
  loading,
}) => {
  const { t } = useTranslation();

  if (!summary && !loading) {
    return (
      <Text c="dimmed" ta="center" py="md">
        {t("dashboardSummary.noDataForMonth")}
      </Text>
    );
  }

  const s = summary;
  const currency = s?.currency || "USD";
  const distanceUnit = s?.distanceUnit || "km";
  const volumeUnit = s?.volumeUnit || "l";

  const hasData =
    (s?.totalCost || 0) > 0 ||
    (s?.refuelsVolume || 0) > 0 ||
    (s?.totalDistance || 0) > 0 ||
    (s?.revenuesAmount || 0) > 0;

  if (!hasData && !loading) {
    return (
      <Text c="dimmed" ta="center" py="md">
        {t("dashboardSummary.noDataForMonth")}
      </Text>
    );
  }

  const refuelsCountLabel =
    s && s.refuelsCount > 0 ? String(s.refuelsCount) : undefined;
  const maintenanceCountLabel =
    s && s.maintenanceCount > 0 ? String(s.maintenanceCount) : undefined;
  const otherExpensesCountLabel =
    s && s.otherExpensesCount > 0 ? String(s.otherExpensesCount) : undefined;
  const revenueCountLabel =
    s && s.revenuesCount > 0 ? String(s.revenuesCount) : undefined;

  return (
    <Stack gap="md">
      {/* Row 1: Cost breakdown */}
      <SimpleGrid cols={{ base: 2, xs: 4 }} spacing="xs">
        <SummaryStat
          iconName="GasStation"
          label={t("dashboardSummary.refuels")}
          value={formatCurrency(s?.refuelsCost || 0, currency)}
          subValue={refuelsCountLabel}
          color="cyan"
          loading={loading}
        />
        <SummaryStat
          iconName="Tool"
          label={t("dashboardSummary.maintenance")}
          value={formatCurrency(s?.maintenanceCost || 0, currency)}
          subValue={maintenanceCountLabel}
          color="orange"
          loading={loading}
        />
        <SummaryStat
          iconName="Receipt"
          label={t("dashboardSummary.otherExpenses")}
          value={formatCurrency(s?.otherExpensesCost || 0, currency)}
          subValue={otherExpensesCountLabel}
          color="violet"
          loading={loading}
        />
        <SummaryStat
          iconName="CurrencyDollar"
          label={t("dashboardSummary.totalCost")}
          value={formatCurrency(s?.totalCost || 0, currency)}
          color="red"
          loading={loading}
        />
      </SimpleGrid>

      {/* Row 2: Usage metrics */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
        <SummaryStat
          iconName="Droplet"
          label={t("dashboardSummary.fuelPurchased")}
          value={formatVolumeValue(s?.refuelsVolume || 0, volumeUnit)}
          color="blue"
          loading={loading}
        />
        <SummaryStat
          iconName="Road"
          label={t("dashboardSummary.distanceDriven")}
          value={formatDistanceValue(s?.totalDistance || 0, distanceUnit)}
          color="grape"
          loading={loading}
        />
        <SummaryStat
          iconName="Gauge"
          label={t("dashboardSummary.fuelCostPerDistance")}
          value={formatCostPerDistance(
            s?.fuelCostPerDistance ?? null,
            currency,
            distanceUnit
          )}
          color="teal"
          loading={loading}
        />
        <SummaryStat
          iconName="ReportMoney"
          label={t("dashboardSummary.runningCostPerDistance")}
          value={formatCostPerDistance(
            s?.runningCostPerDistance ?? null,
            currency,
            distanceUnit
          )}
          color="pink"
          loading={loading}
        />
      </SimpleGrid>

      {/* Row 3: Revenue */}
      <Group justify="center">
        <SummaryStat
          iconName="Cash"
          label={t("dashboardSummary.revenue")}
          value={formatCurrency(s?.revenuesAmount || 0, currency)}
          subValue={revenueCountLabel}
          color="green"
          loading={loading}
          positive
        />
      </Group>
    </Stack>
  );
};

// ============================================
// Averages Stats Content
// ============================================

interface AveragesStatsContentProps {
  aggregates: AveragesAggregates;
  loading: boolean;
}

const AveragesStatsContent: React.FC<AveragesStatsContentProps> = ({
  aggregates,
  loading,
}) => {
  const { t } = useTranslation();
  const { distanceUnit, volumeUnit, currency } = aggregates;

  const hasData =
    aggregates.totalCost > 0 ||
    aggregates.refuelsVolume > 0 ||
    aggregates.totalDistance > 0 ||
    aggregates.revenuesAmount > 0;

  if (!hasData && !loading) {
    return (
      <Text c="dimmed" ta="center" py="md">
        {t("dashboardSummary.noDataForMonth")}
      </Text>
    );
  }

  return (
    <Stack gap="md">
      {/* Row 1: Total monthly expense */}
      <Group justify="center">
        <SummaryStat
          iconName="CurrencyDollar"
          label={t("dashboardSummary.avgMonthlyExpense")}
          value={formatCurrency(aggregates.totalCost, currency)}
          color="red"
          loading={loading}
        />
      </Group>

      {/* Row 2: Usage metrics */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
        <SummaryStat
          iconName="Droplet"
          label={t("dashboardSummary.avgFuelPurchased")}
          value={formatVolumeValue(aggregates.refuelsVolume, volumeUnit)}
          color="blue"
          loading={loading}
        />
        <SummaryStat
          iconName="Road"
          label={t("dashboardSummary.avgDistanceDriven")}
          value={formatDistanceValue(aggregates.totalDistance, distanceUnit)}
          color="grape"
          loading={loading}
        />
        <SummaryStat
          iconName="Gauge"
          label={t("dashboardSummary.fuelCostPerDistance")}
          value={formatCostPerDistance(
            aggregates.fuelCostPerDistance,
            currency,
            distanceUnit
          )}
          color="teal"
          loading={loading}
        />
        <SummaryStat
          iconName="ReportMoney"
          label={t("dashboardSummary.runningCostPerDistance")}
          value={formatCostPerDistance(
            aggregates.runningCostPerDistance,
            currency,
            distanceUnit
          )}
          color="pink"
          loading={loading}
        />
      </SimpleGrid>

      {/* Row 3: Revenue */}
      <Group justify="center">
        <SummaryStat
          iconName="Cash"
          label={t("dashboardSummary.avgRevenue")}
          value={formatCurrency(aggregates.revenuesAmount, currency)}
          color="green"
          loading={loading}
          positive
        />
      </Group>
    </Stack>
  );
};

// ============================================
// Main Component
// ============================================

/**
 * Fleet-wide summary bar with month-by-month navigation.
 *
 * Data sources:
 * - Monthly view: uses `useFleetSummary` hook (dedicated `dashboardFleetSummaryGet` API).
 *   Supports navigating to any past month. Past months are cached (no re-fetch).
 *   Current month is always re-fetched.
 * - Averages view: uses `useDashboardContext` (already loaded by dashboard).
 *   Aggregates rolling averages across all vehicles client-side.
 */
export const DashboardSummaryBar: React.FC<DashboardSummaryBarProps> = ({
  className,
}) => {
  const { t } = useTranslation(["common"]);

  // Dashboard context for averages (already loaded)
  const { items, loading: dashboardLoading } = useDashboardContext();

  // Fleet summary hook for monthly data with navigation
  const {
    summary,
    loading: fleetLoading,
    selectedMonth,
    isCurrentMonth,
    canGoToPreviousMonth,
    canGoToNextMonth,
    goToPreviousMonth,
    goToNextMonth,
    goToCurrentMonth,
  } = useFleetSummary({ autoLoad: true });

  const [mode, setMode] = useState<SummaryMode>("monthly");

  // ── Averages (from dashboard context, aggregated client-side) ──

  const averagesAggregates = useMemo((): AveragesAggregates => {
    if (items.length === 0) return EMPTY_AVERAGES;
    return aggregateAverages(items);
  }, [items]);

  // ── Mode selector labels ──

  const avgMonthsCount = useMemo(() => {
    const firstItem = items[0];
    return firstItem?.averages?.monthsCount || 0;
  }, [items]);

  const modeData = useMemo(
    () => [
      {
        label: t("common:stats.monthly"),
        value: "monthly" as SummaryMode,
      },
      {
        label: t("common:stats.avg", { count: avgMonthsCount }),
        value: "average" as SummaryMode,
      },
    ],
    [t, avgMonthsCount]
  );

  return (
    <Paper shadow="sm" radius="md" p="md" withBorder className={className}>
      <Stack gap="md">
        {/* Mode selector: Monthly vs Averages */}
        <SegmentedControl
          value={mode}
          onChange={(val) => setMode(val as SummaryMode)}
          data={modeData}
          size="xs"
          fullWidth
          styles={{
            root: {
              backgroundColor: "var(--mantine-color-gray-1)",
            },
          }}
        />

        {/* Month navigator (only in monthly mode) */}
        {mode === "monthly" && (
          <>
            <MonthNavigator
              year={selectedMonth.year}
              month={selectedMonth.month}
              isCurrentMonth={isCurrentMonth}
              canGoToPreviousMonth={canGoToPreviousMonth}
              canGoToNextMonth={canGoToNextMonth}
              onPreviousMonth={goToPreviousMonth}
              onNextMonth={goToNextMonth}
              onCurrentMonth={goToCurrentMonth}
              loading={fleetLoading}
            />
            <Divider />
          </>
        )}

        {/* Averages header */}
        {mode === "average" && (
          <>
            <Text fw={600} size="lg" ta="center">
              {t("common:stats.avgMonths", { count: avgMonthsCount })}
            </Text>
            <Divider />
          </>
        )}

        {/* Stats Content */}
        {mode === "monthly" ? (
          <MonthlyStatsContent summary={summary} loading={fleetLoading} />
        ) : (
          <AveragesStatsContent
            aggregates={averagesAggregates}
            loading={dashboardLoading}
          />
        )}
      </Stack>
    </Paper>
  );
};